import React, { useEffect, useState, useCallback } from 'react';
import { Clock3, RefreshCw, Search, ChevronDown, Download } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { exportToExcel } from '../utils/excelExport';

const BUCKETS = [
  ['today', 'Today'],
  ['yesterday', 'Yesterday'],
  ['day_before', 'Day Before Yesterday'],
  ['others', 'Others'],
];

const PAGE_SIZE = 25;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB'); } catch { return '—'; }
};

const PendingJobs = () => {
  const { token } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('today');
  const [client, setClient] = useState('');
  const [allClients, setAllClients] = useState([]);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clientDropdownFilter, setClientDropdownFilter] = useState('');
  const [search, setSearch] = useState(''); // matches customer / job number / job name
  const [page, setPage] = useState(1);

  const load = useCallback(async (clientVal = '') => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/pending-jobs', {
        headers: { Authorization: `Bearer ${token}` },
        params: clientVal ? { client: clientVal } : {},
      });
      setData(res.data);
      // Capture the full client list once (unfiltered call) so the dropdown stays stable.
      if (!clientVal && Array.isArray(res.data?.clients)) setAllClients(res.data.clients);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load pending jobs');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Reset to first page whenever the bucket or search changes.
  useEffect(() => { setPage(1); }, [tab, search]);

  const counts = data?.counts || {};
  const rawRows = data?.buckets?.[tab] || [];
  // Client-side filter across Customer (client), Job Number (job_code) and Job Name (title).
  const q = search.trim().toLowerCase();
  const allRows = !q ? rawRows : rawRows.filter((r) =>
    (r.client || '').toLowerCase().includes(q) ||
    (r.job_code || '').toLowerCase().includes(q) ||
    (r.title || '').toLowerCase().includes(q)
  );
  const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const rows = allRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const fromRow = allRows.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const toRow = Math.min(safePage * PAGE_SIZE, allRows.length);

  const tabLabel = (BUCKETS.find(([k]) => k === tab) || [tab, tab])[1];

  // Export the currently active bucket (respecting the search filter) as Excel/CSV.
  const exportBucket = () => {
    if (allRows.length === 0) { alert('No jobs to export in this list.'); return; }
    const rowsForExport = allRows.map((r, i) => ({
      '#': i + 1,
      'Job Code': r.job_code || '',
      'Job Name': r.title || '',
      'Client': r.client || '',
      'Complexity': (r.complexity || '').replace(/_/g, ' '),
      'Booked': fmtDate(r.created_ist || r.created_at),
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    exportToExcel(rowsForExport, `pending_jobs_${tab}_${stamp}.csv`);
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
          <Clock3 className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pending Jobs</h1>
          <p className="text-sm text-gray-500">Preflight &amp; Prepress jobs waiting to start Prepress QC, by booking date.</p>
        </div>
        <button
          onClick={exportBucket}
          className="ml-auto flex items-center gap-2 px-3 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700"
          title={`Export the “${tabLabel}” list to Excel`}
        >
          <Download className="w-4 h-4" /> Export {tabLabel} (Excel)
        </button>
        <button
          onClick={() => load(client)}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Filters: client (searchable dropdown) + search by Customer / Job Number / Job Name */}
      <div className="flex flex-wrap items-center gap-2 my-4">
        <div className="relative w-64">
          <button
            type="button"
            onClick={() => setClientDropdownOpen((s) => !s)}
            className="w-full text-left px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between bg-white"
          >
            <span className={`truncate ${client ? 'text-gray-800' : 'text-gray-400'}`}>
              {client || 'Select Client'}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
          </button>

          {clientDropdownOpen && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-auto">
              <div className="p-2 sticky top-0 bg-white">
                <input
                  type="text"
                  placeholder="Filter by client name..."
                  value={clientDropdownFilter}
                  onChange={(e) => setClientDropdownFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              <div className="divide-y divide-gray-100">
                <button
                  type="button"
                  onClick={() => { setClient(''); setClientDropdownOpen(false); setClientDropdownFilter(''); load(''); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                    !client ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-500 italic'
                  }`}
                >
                  No Client
                </button>
                {allClients
                  .filter((c) => clientDropdownFilter === '' || (c || '').toLowerCase().includes(clientDropdownFilter.toLowerCase()))
                  .map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => { setClient(c); setClientDropdownOpen(false); setClientDropdownFilter(''); load(c); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                        client === c ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                {allClients.filter((c) => clientDropdownFilter === '' || (c || '').toLowerCase().includes(clientDropdownFilter.toLowerCase())).length === 0 && (
                  <div className="px-3 py-3 text-sm text-gray-500">No clients match.</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer, job number or job name"
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg w-72"
          />
        </div>

        {(client || search) && (
          <button
            onClick={() => { setSearch(''); if (client) { setClient(''); load(''); } }}
            className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear
          </button>
        )}
      </div>

      {/* Date bucket tabs with counts */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-4">
        {BUCKETS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
            }`}
          >
            {label}
            <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${tab === key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
              {counts[key] || 0}
            </span>
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="py-10 text-center text-gray-500 animate-pulse">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
          {q ? 'No matching jobs found.' : 'No pending jobs in this bucket.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left">
                <th className="px-4 py-2 font-semibold text-gray-700">#</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Job</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Client</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Complexity</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Booked</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-400">{(safePage - 1) * PAGE_SIZE + i + 1}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-800">{r.job_code || '—'}</div>
                    <div className="text-xs text-gray-500">{r.title || 'Untitled'}</div>
                  </td>
                  <td className="px-4 py-2 text-gray-600">{r.client || '—'}</td>
                  <td className="px-4 py-2 text-gray-600 capitalize">{(r.complexity || '').replace(/_/g, ' ') || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{fmtDate(r.created_ist || r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {allRows.length > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Showing {fromRow}–{toRow} of {allRows.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >← Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '…' ? (
                      <span key={`el-${i}`} className="px-1 text-gray-400 text-sm">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium ${
                          p === safePage ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                      >{p}</button>
                    )
                  )}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >Next →</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PendingJobs;
